/**
 * PATH: packages/ui/src/contextMenu/ContextMenuRoot.tsx
 * FILE_ID: UI-CTXMENU-POSITIONED-ROOT-001
 * PURPOSE: The root `<ContextMenu>` compound-menu panel — click-point
 *          placement, re-anchor-on-resize geometry, and the scope-aware
 *          outside click/scroll close. Split out of PositionedContextMenu.tsx
 *          (change #269 D4 500-line ceiling split).
 * ROLE IN ARCHITECTURE: Root primitive; ContextMenuSub (a sibling module)
 *          reads the z-index this root provides via MenuZIndexContext.
 * MAIN EXPORTS: ContextMenu, ContextMenuProps.
 * NON-RESPONSIBILITIES: Item/Header/Separator rendering (ContextMenuPrimitives.tsx);
 *          submenu placement (ContextMenuSub.tsx).
 * RELATED: See PositionedContextMenu.tsx for the full B4 convergence history
 *          and the four vendored copies this consolidated.
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { computeMenuPosition } from './contextMenuGeometry';
import { usePositionedMenuKeyboard } from './usePositionedMenuKeyboard';
import { DEFAULT_Z_INDEX_BASE, MenuZIndexContext } from './positionedShared';

export interface ContextMenuProps {
  x: number;
  y: number;
  onClose: () => void;
  /** Overlay z-scale base (backdrop = base, root panel = base+1,
   *  portaled submenu panels = base+2). Default 110. */
  zIndexBase?: number;
  /** Accessible menu name and optional trigger receiving focus on close. */
  label?: string;
  restoreFocusTo?: HTMLElement | null;
  /** Exact viewport gutter (px) the root panel must keep on every edge.
   *  When set, max-height becomes `calc(100vh - 2*gutter)` and the
   *  re-anchor clamp uses the same margin (change #269: reader lane
   *  menus pin a 16px gutter). Omitted → legacy 85vh / 8px behavior. */
  viewportGutter?: number;
  children: ReactNode;
}

export function ContextMenu({
  x,
  y,
  onClose,
  zIndexBase = DEFAULT_Z_INDEX_BASE,
  label = 'Context menu',
  restoreFocusTo,
  viewportGutter,
  children,
}: ContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  usePositionedMenuKeyboard({ menuRef: ref, onClose, restoreFocusTo });
  // Render at clickX/clickY first so the panel can measure itself, then
  // re-anchor via computeMenuPosition (flip-up/flip-left when cramped —
  // the tested geometry rules). Keeping the FIRST render at the click
  // point prevents a visible jump in the common fits-fine case.
  const [position, setPosition] = useState<{ left: number; top: number }>({ left: x, top: y });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reanchor = () => {
      const rect = el.getBoundingClientRect();
      // jsdom returns all-zero rects; skip the re-anchor so tests assert
      // against the raw click point.
      if (rect.width === 0 && rect.height === 0) return;
      const next = computeMenuPosition({
        clickX: x,
        clickY: y,
        menuWidth: rect.width,
        menuHeight: rect.height,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        ...(viewportGutter !== undefined ? { margin: viewportGutter } : {}),
      });
      setPosition({ left: next.left, top: next.top });
    };
    reanchor();
    // BUG-2026-07-09-EMAIL-CTXMENU-CLIPS — the one-shot measure above is
    // not enough: menus with async rows (email menu's category / AI /
    // member submenus resolve from react-query AFTER mount) GROW past the
    // measured height and clip below the fold. Re-clamp whenever the
    // panel's rendered size changes so the menu stays fully in-viewport.
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(reanchor);
    ro.observe(el);
    return () => ro.disconnect();
  }, [x, y, viewportGutter]);

  // BUG-2026-05-28-KANBAN-SUBMENU-SCROLL-CLOSES-MENU.
  //
  // Scope check uses a `data-quotech-cmenu` attribute marker rather
  // than DOM-tree `.contains()`: <ContextMenuSub> portals its panel to
  // `document.body` (to escape parent overflow clipping), so the panel
  // is no longer a DOM descendant of this root — `.contains()` returned
  // false for events inside a submenu, silently dismissing the entire
  // menu on every internal scroll. The marker travels with the portaled
  // DOM, so `target.closest('[data-quotech-cmenu]')` finds the scope
  // regardless of where the panel lives in the actual tree.
  //
  // BUG-2026-07-21-EMAIL-SIGNAL-STRIP-MENU-INVISIBLE (R2): defer arming
  // outside listeners until after the opening click/scroll tick. Mounting
  // in an overflow reader can emit a capture-phase scroll (or re-deliver
  // the opening click) that would call onClose immediately — "… shows
  // nothing." Same pattern as AnchoredPopover / AllocateChip.
  useEffect(() => {
    const isInsideMenuScope = (target: EventTarget | null): boolean => {
      if (!target || !(target instanceof Element)) return false;
      return target.closest('[data-quotech-cmenu]') != null;
    };
    let armed = false;
    const armTimer = window.setTimeout(() => {
      armed = true;
    }, 0);
    const close = (e: Event) => {
      if (!armed) return;
      if (isInsideMenuScope(e.target)) return;
      onClose();
    };
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    return () => {
      window.clearTimeout(armTimer);
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [onClose]);

  // Root must portal to document.body — same invariant as tag/allocation
  // pickers (BUG-2026-07-17-TAG-PICKER-CLIPPED / ALLOCATION-DROPDOWN-CLIPPED).
  // In-tree `position:fixed` still clips when a reader ancestor establishes
  // a containing block (overflow + transform/filter/backdrop-blur).
  const portalTarget = typeof document !== 'undefined' ? document.body : null;
  if (!portalTarget) return null;

  return createPortal(
    <MenuZIndexContext.Provider value={zIndexBase}>
      <div
        className="fixed inset-0"
        style={{ zIndex: zIndexBase }}
        data-quotech-cmenu="backdrop"
        onPointerDown={onClose}
        onClick={onClose}
        onContextMenu={(e) => {
          e.preventDefault();
          onClose();
        }}
      >
        <div
          ref={ref}
          role="menu"
          tabIndex={-1}
          aria-label={label}
          data-quotech-cmenu="root"
          // No enter animation: compositor fade/zoom can stall at the
          // 0% keyframe and leave a mounted menu invisible. Resting
          // opacity is always 1.
          className="fixed rounded-lg border border-border bg-card shadow-xl p-1"
          // Critical layout props INLINE (not Tailwind arbitrary-value
          // classes): this file renders inside apps whose CSS build may
          // not @source-scan every package, and Tailwind v4 JIT silently
          // drops un-scanned arbitrary classes (BUG-2026-05-28-KANBAN-
          // SUBMENU-NOT-SCROLLABLE). Inline styles can't be dropped.
          style={{
            left: position.left,
            top: position.top,
            zIndex: zIndexBase + 1,
            minWidth: 200,
            maxWidth: 320,
            maxHeight:
              viewportGutter !== undefined
                ? `calc(100vh - ${viewportGutter * 2}px)`
                : '85vh',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    </MenuZIndexContext.Provider>,
    portalTarget
  );
}
